import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Card,
  Input,
  Button,
  Row,
  Col,
  Empty,
  Spin,
  Tag,
  Typography,
  Space,
  Drawer,
  message,
} from "antd"
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import { DiffEditor, Editor } from "@monaco-editor/react"
import {
  setToken,
  integrationAuditListHistory,
  integrationAuditListOwner,
} from "../../services/integration"
import { loadStorage } from "../../services/storage"
import { STORAGE_KEY } from "../../context/AuthContext"
import { IntegrationAuditListHistoryResponse, IntegrationAuditListOwnerResponse } from "services/integration/interfaces/ApiResponse"

const { Text } = Typography

// Page size for each lazily-loaded chunk.
const PAGE_SIZE = 20

type AuditEntry = IntegrationAuditListHistoryResponse["entries"][number]
type OwnerEntry = IntegrationAuditListOwnerResponse["entries"][number]

const extractEntries = (response: any) => {
  const body = response?.data
  return body?.data?.entries ?? []
}

const extractRef = (response: any): string | undefined => {
  const body = response?.data
  return body?.meta?.ref
}

// Full current entity, returned only when `include_current` is requested.
const extractCurrent = (response: any): any => {
  const body = response?.data
  return body?.data?.current
}

const prettyJson = (value: any) => {
  try {
    return JSON.stringify(value ?? {}, null, 2)
  } catch {
    return String(value)
  }
}

const formatDate = (value?: string) => {
  if (!value) return null
  const date = new Date(value)
  return isNaN(date.getTime()) ? value : date.toLocaleString()
}

const ownerLabel = (owner?: OwnerEntry) => {
  if (!owner) return null
  const name = [owner.first_name, owner.last_name].filter(Boolean).join(" ")

  return `${name} (${owner.email})`
}

const IntegrationAudit: React.FC<any> = () => {
  const [uuidInput, setUuidInput] = useState("")
  const [parentUuid, setParentUuid] = useState("")
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [owners, setOwners] = useState<Record<string, OwnerEntry>>({})
  const [currentEntity, setCurrentEntity] = useState<any>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Set the integration API token once on mount.
  const tokenReady = useRef(false)
  if (!tokenReady.current) {
    const store = loadStorage(STORAGE_KEY)
    if (store) setToken(store.accessToken)
    tokenReady.current = true
  }

  // Toggle the current-entity drawer with alt+s (only once an entity loaded).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === "s" || e.key === "S")) {
        e.preventDefault()
        if (currentEntity) setDrawerOpen((open) => !open)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [currentEntity])

  // Guards against overlapping lazy-load requests.
  const fetchingRef = useRef(false)

  const resolveOwners = useCallback(
    async (list: AuditEntry[], known: Record<string, OwnerEntry>) => {
      const missing = Array.from(
        new Set(
          list
            .map((e) => e.owner_user_uuid)
            .filter((uuid) => uuid && !known[uuid])
        )
      )
      if (!missing.length) return
      try {
        const response = await integrationAuditListOwner({
          uuid_list: missing,
        })
        const resolved: OwnerEntry[] = extractEntries(response) as any
        if (resolved?.length) {
          setOwners((prev) => {
            const next = { ...prev }
            resolved.forEach((o) => {
              if (o?.uuid) next[o.uuid] = o
            })
            return next
          })
        }
      } catch {
        // Owner resolution is best-effort fall back to the raw uuid.
      }
    },
    []
  )

  const fetchChunk = useCallback(
    async (targetUuid: string, ref?: string, includeCurrent?: boolean) => {
      const response = await integrationAuditListHistory({
        parent_uuid: targetUuid,
        size: PAGE_SIZE,
        ...(ref ? { ref } : {}),
        ...(includeCurrent ? { include_current: true } : {}),
      })

      if (!(response as any)?.ok && (response as any)?.problem) {
        throw new Error("Request failed")
      }

      const newEntries = extractEntries(response)
      const nextRef = extractRef(response)
      const current = includeCurrent ? extractCurrent(response) : undefined
      return { newEntries, nextRef, current }
    },
    []
  )

  const handleSearch = useCallback(async () => {
    const target = uuidInput.trim()
    if (!target) {
      message.warning("Please enter a record UUID")
      return
    }
    if (fetchingRef.current) return

    fetchingRef.current = true
    setLoading(true)
    setEntries([])
    setSelectedIndex(0)
    setCursor(undefined)
    setHasMore(false)
    setCurrentEntity(null)
    setParentUuid(target)

    try {
      // Request the full current entity on the first chunk only.
      const { newEntries, nextRef, current } = await fetchChunk(
        target,
        undefined,
        true
      )
      setEntries(newEntries)
      setCursor(nextRef)
      setHasMore(Boolean(nextRef) && newEntries.length > 0)
      setCurrentEntity(current ?? null)
      if (!newEntries.length) {
        message.info("No audit history found for this UUID")
      }
      resolveOwners(newEntries, {})
    } catch {
      message.error("Failed to load audit history")
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [uuidInput, fetchChunk, resolveOwners])

  const loadMore = useCallback(async () => {
    if (fetchingRef.current || !hasMore || !cursor || !parentUuid) return

    fetchingRef.current = true
    setLoadingMore(true)
    try {
      const { newEntries, nextRef } = await fetchChunk(parentUuid, cursor)
      setEntries((prev) => {
        const merged = [...prev, ...newEntries]
        resolveOwners(newEntries, owners)
        return merged
      })
      setCursor(nextRef)
      setHasMore(Boolean(nextRef) && newEntries.length > 0)
    } catch {
      message.error("Failed to load more entries")
    } finally {
      setLoadingMore(false)
      fetchingRef.current = false
    }
  }, [hasMore, cursor, parentUuid, fetchChunk, resolveOwners, owners])

  // Lazy load when the user scrolls near the bottom of the list.
  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        loadMore()
      }
    },
    [loadMore]
  )

  const selected = entries[selectedIndex]

  // Entries are ordered newest -> oldest (DESC). A lower index is a newer
  // version a higher index is an older version.
  const goNewer = useCallback(() => {
    setSelectedIndex((i) => Math.max(0, i - 1))
  }, [])

  const goOlder = useCallback(() => {
    setSelectedIndex((i) => {
      const next = Math.min(entries.length - 1, i + 1)
      // Reaching the end of the loaded list triggers the next chunk.
      if (next >= entries.length - 1 && hasMore) loadMore()
      return next
    })
  }, [entries.length, hasMore, loadMore])

  // The loaded record's canonical (entity type), taken from the full current
  // entity returned with the first chunk (include_current).
  const entityCanonical = currentEntity?.canonical
    ? String(currentEntity.canonical)
    : undefined

  const diffOriginal = useMemo(
    () => prettyJson(selected?.previous_data),
    [selected]
  )
  const diffModified = useMemo(
    () => prettyJson(selected?.current_data),
    [selected]
  )

  return (
    <>
      <Card title="Integration Audit History" style={{ marginBottom: 25 }}>
        <Row gutter={9}>
          <Col flex="auto">
            <Input
              placeholder="Enter record UUID"
              value={uuidInput}
              allowClear
              onChange={(e) => setUuidInput(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={loading}
              onClick={handleSearch}
            >
              Load history
            </Button>
          </Col>
        </Row>
        {parentUuid && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ marginRight: 8 }}>
              Loaded record:
            </Text>
            <Text strong copyable code>
              {parentUuid}
            </Text>
            {entityCanonical && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({entityCanonical})
              </Text>
            )}
            {/* Current entity is opened via the alt+s shortcut (see below). */}
            {/* {currentEntity && (
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setDrawerOpen(true)}
              >
                View current entity
              </Button>
            )} */}
          </div>
        )}
      </Card>

      <Drawer
        title="Current entity"
        placement="right"
        width={640}
        visible={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        bodyStyle={{ padding: 0 }}
      >
        <Editor
          height="100%"
          language="json"
          value={prettyJson(currentEntity)}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            fontSize: 12,
            wordWrap: "on",
          }}
        />
      </Drawer>

      <Spin spinning={loading}>
        <Row gutter={16}>
          <Col xs={24} md={8} lg={7}>
            <Card
              title={`Changes${entries.length ? ` (${entries.length})` : ""}`}
              bodyStyle={{ padding: 0 }}
            >
              <div
                style={{ maxHeight: 620, overflowY: "auto" }}
                onScroll={handleListScroll}
              >
                {!entries.length && !loading ? (
                  <Empty
                    description="No changes loaded"
                    style={{ padding: 24 }}
                  />
                ) : (
                  entries.map((entry, index) => {
                    const owner = owners[entry.owner_user_uuid]
                    const isActive = index === selectedIndex
                    return (
                      <div
                        key={`${entry.uuid}-${index}`}
                        onClick={() => setSelectedIndex(index)}
                        style={{
                          padding: "12px 16px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                          borderLeft: isActive
                            ? "3px solid #1890ff"
                            : "3px solid transparent",
                          background: isActive ? "#e6f7ff" : undefined,
                        }}
                      >
                        <Space size={4}>
                          <Tag color="default" style={{"fontFamily": "monospace"}}>
                            v{entry.previous_version} &rarr; v
                            {entry.current_version}
                          </Tag>
                          <Tag color="blue">{entry.source_key}</Tag>
                        </Space>
                        <div style={{ marginTop: 4 }}>
                          <Text type="secondary" style={{ fontSize: 12 }} title={entry.created_date}>
                            {formatDate(entry.created_date)}
                          </Text>
                        </div>
                        <div style={{ marginTop: 6 }}>
                          <Text strong style={{ fontSize: 12 }}>
                            {entry.path}
                          </Text>
                        </div>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {entry.request_hash}
                          </Text>
                        </div>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {ownerLabel(owner) || entry.owner_user_uuid}
                            {owner?.canonical ? ` (${owner.canonical})` : ""}
                          </Text>
                        </div>
                      </div>
                    )
                  })
                )}
                {loadingMore && (
                  <div style={{ textAlign: "center", padding: 12 }}>
                    <Spin size="small" />
                  </div>
                )}
              </div>
            </Card>
          </Col>

          <Col xs={24} md={16} lg={17}>
            <Card
              title={
                selected
                  ? `Diff: v${selected.previous_version} → v${selected.current_version}`
                  : "Diff"
              }
              extra={
                selected ? (
                  <Space>
                    <Button
                      size="small"
                      icon={<ArrowLeftOutlined />}
                      disabled={selectedIndex <= 0}
                      onClick={goNewer}
                    >
                      Newer
                    </Button>
                    <Button
                      size="small"
                      disabled={selectedIndex >= entries.length - 1 && !hasMore}
                      onClick={goOlder}
                    >
                      Older <ArrowRightOutlined />
                    </Button>
                  </Space>
                ) : null
              }
              bodyStyle={{ padding: 0 }}
            >
              {selected ? (
                <DiffEditor
                  height={600}
                  language="json"
                  original={diffOriginal}
                  modified={diffModified}
                  options={{
                    readOnly: true,
                    renderSideBySide: true,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    fontSize: 12,
                  }}
                />
              ) : (
                <Empty
                  description="Select a change to view its diff"
                  style={{ padding: 48 }}
                />
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </>
  )
}

export default IntegrationAudit
